import React, { useEffect } from "react";
import { Box, Skeleton, Card, CardContent } from "@mui/material";
import { StripeAccountCard } from "./StripeAccountCard";
import { PaymentModeForm } from "./PaymentModeForm";
import { usePaymentAccount } from "../../hooks/usePaymentAccount";
import { usePaymentSettings } from "../../hooks/usePaymentSettings";
import { usePatchPaymentSettings } from "../../hooks/usePaymentSettings";
import { usePaymentActions } from "../../hooks/usePaymentActions";
import { usePaymentAccountStore } from "../../store/paymentAccountSlice";
import { usePaymentsSettingsStore } from "../../store/paymentsSettingsSlice";

export const PaymentsSettingsTab: React.FC = () => {
  const { data: account, isLoading: accountLoading } = usePaymentAccount();
  const { data: settings, isLoading: settingsLoading } = usePaymentSettings();
  const { isRedirecting } = usePaymentAccountStore();
  const { initiateOnboarding, openDashboard } = usePaymentActions();
  const patchMutation = usePatchPaymentSettings();
  const {
    draft,
    isDirty,
    fieldErrors,
    formError,
    initDraft,
    updateDraft,
    resetDraft,
    setFormError,
  } = usePaymentsSettingsStore();

  useEffect(() => {
    if (settings) initDraft(settings);
  }, [settings, initDraft]);

  const handleSave = () => {
    if (!draft) return;
    setFormError(null);
    patchMutation.mutate(
      {
        payment_mode: draft.payment_mode,
        deposit_type: draft.deposit_type ?? undefined,
        deposit_value_minor_units: draft.deposit_value_minor_units ?? undefined,
        deposit_percent_bps: draft.deposit_percent_bps ?? undefined,
        cancellation_policy_enabled: draft.cancellation_policy_enabled,
        cancellation_window_hours: draft.cancellation_window_hours ?? undefined,
        cancellation_policy_custom_note:
          draft.cancellation_policy_custom_note ?? undefined,
        deposit_refund_policy: draft.deposit_refund_policy ?? undefined,
      },
      {
        onError: (err: unknown) => {
          const msg =
            err instanceof Error ? err.message : "Failed to save settings";
          setFormError(msg);
        },
      }
    );
  };

  return (
    <Box>
      <StripeAccountCard
        account={account ?? null}
        isLoading={accountLoading}
        isRedirecting={isRedirecting}
        onConnectStripe={() => initiateOnboarding.mutate()}
        onOpenDashboard={() => openDashboard.mutate()}
      />

      {settingsLoading || !draft ? (
        <Card variant="outlined">
          <CardContent>
            <Skeleton variant="text" width="30%" height={28} />
            <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={120}
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>
      ) : (
        <PaymentModeForm
          draft={draft}
          fieldErrors={fieldErrors}
          formError={formError}
          isSaving={patchMutation.isPending}
          isDirty={isDirty}
          onUpdate={updateDraft}
          onSave={handleSave}
          onCancel={() => {
            if (settings) initDraft(settings);
            else resetDraft();
          }}
          paymentsRequired={settings?.payments_required ?? false}
          depositMinFixedMinorUnits={settings?.deposit_min_fixed_minor_units ?? 0}
          depositMinPercentBps={settings?.deposit_min_percent_bps ?? 0}
        />
      )}
    </Box>
  );
};
